import { useEffect, useState } from "react";
import { Navigate, Outlet } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * 관리자 라우트 가드 — 진입 시 백엔드 /api/admin/verify 로 권한을 실시간 재검증.
 * 프론트엔드 캐시(localStorage user)에만 의존하지 않아, 환경 전환·캐시 staleness 로 인한
 * 권한 오판을 방지한다. 비관리자/미인증은 홈으로 리다이렉트.
 */
export default function AdminRoute({ children }) {
  const { token, isAuthenticated, loading } = useAuth();
  const [status, setStatus] = useState("checking"); // checking | ok | denied

  useEffect(() => {
    let active = true;
    if (loading) return;
    if (!isAuthenticated || !token) {
      setStatus("denied");
      return;
    }
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/admin/verify`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json().catch(() => ({}));
        if (!active) return;
        setStatus(res.ok && data.is_admin ? "ok" : "denied");
      } catch {
        if (active) setStatus("denied");
      }
    })();
    return () => {
      active = false;
    };
  }, [loading, isAuthenticated, token]);

  if (loading || status === "checking") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50" data-testid="admin-route-loading">
        <Loader2 className="w-8 h-8 text-burgundy-500 animate-spin" />
      </div>
    );
  }

  if (status === "denied") {
    toast.error("관리자 권한이 필요합니다.");
    return <Navigate to="/" replace />;
  }

  return children ? children : <Outlet />;
}
